import {
  Add,
  Close,
  ContentCopy,
  Lock,
  MoreVert,
  People,
  Public,
  RefreshOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { User } from "../../utils";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Company } from "../../types";
import { hostname, parseError } from "../../utils";

// Visibility constants
const VISIBILITY_PRIVATE = "private";
const VISIBILITY_SHARED = "shared";
const VISIBILITY_PUBLIC = "public";

interface Prompt {
  id: string;
  name: string;
  content: string;
  description: string;
  visibility: string;
  isOwner: boolean;
  created_date: string;
  updated_date: string;
}

interface PromptDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (prompt: Partial<Prompt>) => void;
  existingData: Prompt | null;
  loading: boolean;
}

const PromptDialog: React.FC<PromptDialogProps> = ({
  open,
  onClose,
  onSave,
  existingData,
  loading,
}) => {
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState(VISIBILITY_PRIVATE);

  useEffect(() => {
    if (existingData) {
      setName(existingData.name || "");
      setContent(existingData.content || "");
      setDescription(existingData.description || "");
      setVisibility(existingData.visibility || VISIBILITY_PRIVATE);
    } else {
      setName("");
      setContent("");
      setDescription("");
      setVisibility(VISIBILITY_PRIVATE);
    }
  }, [existingData, open]);

  const handleSave = () => {
    onSave({
      id: existingData?.id,
      name,
      content,
      description,
      visibility,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle sx={{ borderBottom: 1, borderColor: "divider", py: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">
            {existingData ? "Edit Prompt" : "New Prompt"}
          </Typography>
          <Box>
            <Button onClick={onClose} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              color="primary"
              disabled={loading || !name.trim() || !content.trim()}
            >
              {existingData ? "Save Changes" : "Create Prompt"}
            </Button>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: "flex", height: "100%" }}>
        {/* Left Panel - Settings */}
        <Box
          sx={{
            width: 350,
            minWidth: 350,
            borderRight: 1,
            borderColor: "divider",
            p: 3,
            overflowY: "auto",
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Settings
          </Typography>

          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            margin="normal"
            required
            size="small"
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="Brief description of what this prompt does"
            multiline
            rows={3}
            size="small"
          />

          <FormControl component="fieldset" sx={{ mt: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Visibility
            </Typography>
            <RadioGroup
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
            >
              <FormControlLabel
                value={VISIBILITY_PRIVATE}
                control={<Radio size="small" />}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Lock fontSize="small" />
                    <Box>
                      <Typography variant="body2">Private</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Only you can see this prompt
                      </Typography>
                    </Box>
                  </Box>
                }
              />
              <FormControlLabel
                value={VISIBILITY_SHARED}
                control={<Radio size="small" />}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <People fontSize="small" />
                    <Box>
                      <Typography variant="body2">Shared</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Everyone in your company can see this prompt
                      </Typography>
                    </Box>
                  </Box>
                }
              />
              <FormControlLabel
                value={VISIBILITY_PUBLIC}
                control={<Radio size="small" />}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Public fontSize="small" />
                    <Box>
                      <Typography variant="body2">Public</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Anyone can discover and clone this prompt
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </Box>

        {/* Right Panel - Editor */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            p: 3,
            overflow: "hidden",
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Prompt Content
          </Typography>
          <TextField
            value={content}
            onChange={(e) => setContent(e.target.value)}
            fullWidth
            multiline
            placeholder="Enter your prompt here..."
            sx={{
              flex: 1,
              "& .MuiInputBase-root": {
                height: "100%",
                alignItems: "flex-start",
              },
              "& .MuiInputBase-input": {
                height: "100% !important",
                overflow: "auto !important",
              },
            }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const VisibilityChip: React.FC<{ visibility: string }> = ({ visibility }) => {
  switch (visibility) {
    case VISIBILITY_SHARED:
      return (
        <Chip
          icon={<People />}
          label="Shared"
          size="small"
          color="primary"
          variant="outlined"
        />
      );
    case VISIBILITY_PUBLIC:
      return (
        <Chip
          icon={<Public />}
          label="Public"
          size="small"
          color="secondary"
          variant="outlined"
        />
      );
    default:
      return (
        <Chip
          icon={<Lock />}
          label="Private"
          size="small"
          variant="outlined"
        />
      );
  }
};

// Prompt Studio List Component
export default ({
  currentUser,
  getToken,
  openDialog,
  eventListener,
  companyId,
  company,
  currentPlan,
}: {
  getToken: Function;
  openDialog: Function;
  eventListener: Function;
  currentUser: any | User;
  companyId: string;
  company: Company;
  currentPlan: string;
}) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [publicPrompts, setPublicPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPrompts, setTotalPrompts] = useState(0);
  const [currentTab, setCurrentTab] = useState<"my" | "public">("my");
  const [publicPage, setPublicPage] = useState(0);
  const [publicTotal, setPublicTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch prompts when component mounts
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchPrompts();
      setLoading(false);
    };
    loadData();
  }, [companyId, page, pageSize]);

  // Fetch public prompts when switching to public tab
  useEffect(() => {
    if (currentTab === "public") {
      fetchPublicPrompts();
    }
  }, [currentTab, publicPage, searchQuery]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchPrompts = async () => {
    setRefreshing(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/content/prompts`, {
        params: { companyId, page: page + 1, pageSize },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data) {
        setPrompts(response.data.items || []);
        setTotalPrompts(response.data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching prompts:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setRefreshing(false);
    }
  };

  const fetchPublicPrompts = async () => {
    setRefreshing(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/content/prompts/public`, {
        params: { page: publicPage + 1, pageSize, search: searchQuery || undefined },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data) {
        setPublicPrompts(response.data.items || []);
        setPublicTotal(response.data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching public prompts:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleSavePrompt = async (promptData: Partial<Prompt>) => {
    setLoading(true);
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/content/prompts`,
        {
          companyId,
          prompt: promptData,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Refresh the prompts list
      fetchPrompts();
    } catch (error: any) {
      console.error("Error saving prompt:", error);
      const errorMsg = error.response?.data?.error || parseError(error);
      eventListener({
        type: "SET_ERROR",
        error: errorMsg,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClonePrompt = async (promptId: string) => {
    setLoading(true);
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/content/prompts/${promptId}/clone`,
        { companyId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      eventListener({
        type: "SET_SUCCESS",
        message: "Prompt cloned to your library",
      });

      // Switch to My Prompts tab and refresh
      setCurrentTab("my");
      fetchPrompts();
    } catch (error) {
      console.error("Error cloning prompt:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePrompt = async (promptId: string) => {
    openDialog(
      "Delete Prompt",
      `Are you sure you want to delete this prompt?`,
      "Confirm",
      async () => {
        setLoading(true);
        try {
          const token = await getToken();
          await axios.delete(`${hostname}/content/prompts/${promptId}`, {
            params: { companyId },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          // Refresh the prompts list
          fetchPrompts();
        } catch (error) {
          console.error("Error deleting prompt:", error);
          eventListener({
            type: "SET_ERROR",
            error: parseError(error),
          });
        } finally {
          setLoading(false);
        }
      }
    );
  };

  const handleCopyPrompt = (prompt: Prompt) => {
    navigator.clipboard.writeText(prompt.content);
    eventListener({
      type: "SET_SUCCESS",
      message: "Prompt copied to clipboard",
    });
  };

  // Main Return
  return (
    <Box sx={{ height: "100%", width: "100%", mt: 4 }}>
      {loading && <LinearProgress sx={{ mt: 2 }} />}

      <Tabs
        value={currentTab}
        onChange={(e, v) => setCurrentTab(v)}
        sx={{ mb: 2 }}
      >
        <Tab label="My Prompts" value="my" />
        <Tab label="Browse Public" value="public" />
      </Tabs>

      {!loading && currentTab === "my" && (
        <>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Box>
              <Button
                data-tutorial="create-prompt"
                variant="contained"
                color="primary"
                onClick={() => setEditorOpen(true)}
                startIcon={<Add />}
                sx={{ mr: 1 }}
              >
                New Prompt
              </Button>
            </Box>
          </Box>

          {prompts.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h6" gutterBottom>
                No Prompts Yet
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Create your first saved prompt to use in your chat
                conversations.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setEditorOpen(true)}
                startIcon={<Add />}
              >
                Create First Prompt
              </Button>
            </Paper>
          ) : (
            <Paper
              style={{ marginTop: 20, marginBottom: 40 }}
              variant="outlined"
            >
              {refreshing && <LinearProgress />}
              {!refreshing && <div style={{ height: 4 }}></div>}
              <IconButton
                onClick={() => fetchPrompts()}
                style={{ float: "right", marginRight: 5 }}
              >
                <RefreshOutlined />
              </IconButton>
              <TableContainer data-tutorial="prompt-list">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Visibility</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {prompts.map((prompt) => (
                      <TableRow key={prompt.id} hover>
                        <TableCell>{prompt.name}</TableCell>
                        <TableCell>
                          {prompt.description
                            ? prompt.description.substring(0, 100) +
                              (prompt.description.length > 100 ? "..." : "")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <VisibilityChip visibility={prompt.visibility} />
                        </TableCell>
                        <TableCell>
                          {prompt.created_date
                            ? moment(prompt.created_date).format("MMMM Do YYYY")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setAnchorEl(e.currentTarget);
                              setSelectedId(prompt.id);
                            }}
                          >
                            <MoreVert />
                          </IconButton>
                          <Menu
                            anchorEl={anchorEl}
                            open={selectedId === prompt.id}
                            onClose={(e: any) => {
                              e.stopPropagation();
                              setAnchorEl(null);
                              setSelectedId(null);
                            }}
                          >
                            <MenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyPrompt(prompt);
                                setAnchorEl(null);
                                setSelectedId(null);
                              }}
                            >
                              <ContentCopy sx={{ mr: 1 }} fontSize="small" />
                              Copy to Clipboard
                            </MenuItem>
                            {prompt.isOwner && (
                              <MenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedPrompt(prompt);
                                  setEditorOpen(true);
                                  setAnchorEl(null);
                                  setSelectedId(null);
                                }}
                              >
                                Edit
                              </MenuItem>
                            )}
                            {prompt.isOwner && (
                              <MenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeletePrompt(prompt.id);
                                  setAnchorEl(null);
                                  setSelectedId(null);
                                }}
                              >
                                Delete
                              </MenuItem>
                            )}
                          </Menu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={totalPrompts}
                  rowsPerPage={pageSize}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {!loading && currentTab === "public" && (
        <>
          <Box sx={{ mb: 2 }}>
            <TextField
              label="Search public prompts"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPublicPage(0);
              }}
              size="small"
              sx={{ width: 300 }}
            />
          </Box>

          {publicPrompts.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h6" gutterBottom>
                No Public Prompts Found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {searchQuery
                  ? "Try a different search term"
                  : "Be the first to share a public prompt!"}
              </Typography>
            </Paper>
          ) : (
            <Paper
              style={{ marginTop: 20, marginBottom: 40 }}
              variant="outlined"
            >
              {refreshing && <LinearProgress />}
              {!refreshing && <div style={{ height: 4 }}></div>}
              <IconButton
                onClick={() => fetchPublicPrompts()}
                style={{ float: "right", marginRight: 5 }}
              >
                <RefreshOutlined />
              </IconButton>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {publicPrompts.map((prompt) => (
                      <TableRow key={prompt.id} hover>
                        <TableCell>{prompt.name}</TableCell>
                        <TableCell>
                          {prompt.description
                            ? prompt.description.substring(0, 100) +
                              (prompt.description.length > 100 ? "..." : "")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {prompt.created_date
                            ? moment(prompt.created_date).format("MMMM Do YYYY")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleClonePrompt(prompt.id)}
                          >
                            Add to My Prompts
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={publicTotal}
                  rowsPerPage={pageSize}
                  page={publicPage}
                  onPageChange={(e, newPage) => setPublicPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setPageSize(parseInt(e.target.value, 10));
                    setPublicPage(0);
                  }}
                />
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {/* Create/Edit Dialog */}
      <PromptDialog
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setSelectedPrompt(null);
        }}
        onSave={handleSavePrompt}
        existingData={selectedPrompt}
        loading={loading}
      />
    </Box>
  );
};
